"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

type HoraLanguage = "en" | "hi";
type HoraNature = "Most Auspicious" | "Auspicious" | "Neutral / Specific" | "Caution Required";

interface HoraDef {
  id: string;
  name: string;
  hindi: string;
  planet: string;
  symbol: string;
  nature: HoraNature;
  bestFor: string[];
  avoidFor: string;
  mantra: string;
}

const HORAS: HoraDef[] = [
  {
    id: "guru",
    name: "Jupiter Hora",
    hindi: "गुरु होरा",
    planet: "Jupiter (Guru 🟡)",
    symbol: "✨",
    nature: "Most Auspicious",
    bestFor: [
      "Initiating new ventures & business inaugurations",
      "Higher education & religious ceremonies",
      "Financial investments & opening accounts",
      "Meeting mentors, teachers & advisors",
    ],
    avoidFor: "Avoid unethical or aggressive confrontations during Guru hora.",
    mantra: "Om Gram Greem Groum Sah Gurave Namah",
  },
  {
    id: "shukra",
    name: "Venus Hora",
    hindi: "शुक्र होरा",
    planet: "Venus (Shukra ⚪)",
    symbol: "💎",
    nature: "Auspicious",
    bestFor: [
      "Purchasing jewelry, luxury garments & vehicles",
      "Marriage proposals, romance & social events",
      "Artistic, creative & entertainment projects",
      "Financial settlements and partnerships",
    ],
    avoidFor: "Avoid hostile arguments or surgical procedures during Shukra hora.",
    mantra: "Om Dram Dreem Droum Sah Shukraya Namah",
  },
  {
    id: "budh",
    name: "Mercury Hora",
    hindi: "बुध होरा",
    planet: "Mercury (Budh 🟢)",
    symbol: "📜",
    nature: "Auspicious",
    bestFor: [
      "Signing contracts, agreements & documentation",
      "Trading, commerce, sales & marketing campaigns",
      "Coding, software development & research",
      "Academic exams and learning new subjects",
    ],
    avoidFor: "Avoid risky speculation when Mercury is retrograde.",
    mantra: "Om Bram Breem Broum Sah Budhaya Namah",
  },
  {
    id: "surya",
    name: "Sun Hora",
    hindi: "सूर्य होरा",
    planet: "Sun (Surya ☀️)",
    symbol: "☀️",
    nature: "Auspicious",
    bestFor: [
      "Government applications, tenders & executive meetings",
      "Taking leadership decisions and political affairs",
      "Purchasing precious gold or copper items",
      "Spiritual Surya Arghya and Vedic prayers",
    ],
    avoidFor: "Avoid travel and soft emotional discussions during Surya hora.",
    mantra: "Om Hram Hreem Hroum Sah Suryaya Namah",
  },
  {
    id: "chandra",
    name: "Moon Hora",
    hindi: "चंद्र होरा",
    planet: "Moon (Chandra 🌙)",
    symbol: "🌙",
    nature: "Auspicious",
    bestFor: [
      "Travel, journeys and water-related activities",
      "Domestic shopping and family gatherings",
      "Public relations and community outreach",
      "Meditation, yoga and emotional grounding",
    ],
    avoidFor: "Avoid signing permanent binding contracts during waning Moon.",
    mantra: "Om Shram Shreem Shroum Sah Chandraya Namah",
  },
  {
    id: "mangal",
    name: "Mars Hora",
    hindi: "मंगल होरा",
    planet: "Mars (Mangal 🔴)",
    symbol: "⚔️",
    nature: "Neutral / Specific",
    bestFor: [
      "Sports, workouts and physical defense training",
      "Purchasing real estate or agricultural land",
      "Resolving legal disputes and technical engineering",
      "Administrative disciplinary actions",
    ],
    avoidFor: "Strictly avoid marriage negotiations or peaceful reconciliations.",
    mantra: "Om Kram Kreem Kroum Sah Bhaumaya Namah",
  },
  {
    id: "shani",
    name: "Saturn Hora",
    hindi: "शनि होरा",
    planet: "Saturn (Shani 🪐)",
    symbol: "🪐",
    nature: "Caution Required",
    bestFor: [
      "Laying building foundations and construction work",
      "Dealing in oil, iron, machinery and scrap",
      "Long-term hard discipline, labor and austerity",
      "Planting trees and agriculture",
    ],
    avoidFor: "Avoid starting auspicious ceremonies, weddings, or medical surgeries.",
    mantra: "Om Pram Preem Proum Sah Shanaischaraya Namah",
  },
];

type HoraText = Pick<HoraDef, "name" | "planet" | "bestFor" | "avoidFor" | "mantra">;

/** Hindi text per hora; the id, symbol and nature stay in HORAS. */
const HORAS_HI: Readonly<Record<string, HoraText>> = {
  guru: {
    name: "गुरु होरा",
    planet: "गुरु (बृहस्पति 🟡)",
    bestFor: [
      "नया काम या व्यापार शुरू करना, उद्घाटन",
      "उच्च शिक्षा और धार्मिक अनुष्ठान",
      "निवेश और नया खाता खोलना",
      "गुरु, शिक्षक और सलाहकार से मिलना",
    ],
    avoidFor: "गुरु होरा में अनैतिक काम या आक्रामक टकराव से बचें।",
    mantra: "ॐ ग्रां ग्रीं ग्रौं सः गुरवे नमः",
  },
  shukra: {
    name: "शुक्र होरा",
    planet: "शुक्र ⚪",
    bestFor: [
      "आभूषण, अच्छे वस्त्र और वाहन ख़रीदना",
      "विवाह प्रस्ताव, प्रेम और सामाजिक आयोजन",
      "कला, रचनात्मक और मनोरंजन के काम",
      "आर्थिक समझौते और साझेदारी",
    ],
    avoidFor: "शुक्र होरा में झगड़े और सर्जरी से बचें।",
    mantra: "ॐ द्रां द्रीं द्रौं सः शुक्राय नमः",
  },
  budh: {
    name: "बुध होरा",
    planet: "बुध 🟢",
    bestFor: [
      "अनुबंध, समझौते और काग़ज़ी काम",
      "व्यापार, बिक्री और मार्केटिंग",
      "कोडिंग, सॉफ़्टवेयर और शोध",
      "परीक्षा और नया विषय सीखना",
    ],
    avoidFor: "बुध वक्री हो तो जोखिम भरे सट्टे से बचें।",
    mantra: "ॐ ब्रां ब्रीं ब्रौं सः बुधाय नमः",
  },
  surya: {
    name: "सूर्य होरा",
    planet: "सूर्य ☀️",
    bestFor: [
      "सरकारी आवेदन, टेंडर और उच्च अधिकारियों से बैठक",
      "नेतृत्व के निर्णय और राजनीतिक काम",
      "सोना या तांबा ख़रीदना",
      "सूर्य अर्घ्य और वैदिक प्रार्थना",
    ],
    avoidFor: "सूर्य होरा में यात्रा और भावुक बातचीत से बचें।",
    mantra: "ॐ ह्रां ह्रीं ह्रौं सः सूर्याय नमः",
  },
  chandra: {
    name: "चंद्र होरा",
    planet: "चंद्र 🌙",
    bestFor: [
      "यात्रा और जल से जुड़े काम",
      "घरेलू ख़रीदारी और पारिवारिक मिलन",
      "जनसंपर्क और सामुदायिक काम",
      "ध्यान, योग और मन की शांति",
    ],
    avoidFor: "घटते चंद्रमा में स्थायी अनुबंध पर हस्ताक्षर से बचें।",
    mantra: "ॐ श्रां श्रीं श्रौं सः चंद्राय नमः",
  },
  mangal: {
    name: "मंगल होरा",
    planet: "मंगल 🔴",
    bestFor: [
      "खेल, व्यायाम और आत्मरक्षा का अभ्यास",
      "ज़मीन या खेती की भूमि ख़रीदना",
      "क़ानूनी विवाद सुलझाना और तकनीकी काम",
      "प्रशासनिक अनुशासन की कार्रवाई",
    ],
    avoidFor: "विवाह की बातचीत या सुलह की कोशिश बिल्कुल न करें।",
    mantra: "ॐ क्रां क्रीं क्रौं सः भौमाय नमः",
  },
  shani: {
    name: "शनि होरा",
    planet: "शनि 🪐",
    bestFor: [
      "नींव रखना और निर्माण कार्य",
      "तेल, लोहा, मशीनरी और कबाड़ का व्यापार",
      "लंबी मेहनत, अनुशासन और तप",
      "पेड़ लगाना और खेती",
    ],
    avoidFor: "शुभ संस्कार, विवाह या सर्जरी शुरू करने से बचें।",
    mantra: "ॐ प्रां प्रीं प्रौं सः शनैश्चराय नमः",
  },
};

const NATURE_HI: Record<HoraNature, string> = {
  "Most Auspicious": "सर्वाधिक शुभ",
  Auspicious: "शुभ",
  "Neutral / Specific": "सामान्य / विशेष कार्य",
  "Caution Required": "सावधानी ज़रूरी",
};

const HORA_COPY = {
  en: {
    eyebrow: "Vedic Timing Shastra",
    title: "Aaj Ka Shubh Hora & Auspicious Timings (आज की शुभ होरा)",
    intro:
      "Check the 7 Planetary Horas of the day to identify auspicious hours for business, finance, career, travel, and religious rituals.",
    governedBy: "Governed by:",
    chaldean: "Order follows the ancient Chaldean sequence.",
    // It opens the free-kundali form with a hora question (C23: it used to
    // promise "Check Today's Hora Timing", a table it never showed).
    cta: "Find your best hora with your kundali →",
    favorable: "Highly Favorable Activities (शुरू करने योग्य कार्य)",
    avoid: "What to Avoid (सावधानी)",
    mantra: "Governing Beej Mantra",
    choghadiyaPrompt: "Need exact sunrise and sunset based Choghadiya and Rahu Kaal timings?",
    choghadiyaLink: "Open Aaj Ka Choghadiya →",
    choghadiyaHref: "/choghadiya/",
    sourcePath: "/panchang",
    prompt:
      "Which is the best Shubh Hora today for my activity according to Aaj Ka Panchang? Please analyze the planetary hour timing for my city.",
  },
  hi: {
    eyebrow: "वैदिक समय शास्त्र",
    title: "आज की शुभ होरा और शुभ समय",
    intro:
      "दिन की 7 ग्रह होराएँ देखें और व्यापार, धन, करियर, यात्रा और पूजा-पाठ के लिए शुभ समय पहचानें।",
    governedBy: "स्वामी ग्रह:",
    chaldean: "क्रम प्राचीन कैल्डियन अनुक्रम के अनुसार है।",
    cta: "अपनी कुंडली से शुभ होरा जानें →",
    favorable: "शुरू करने योग्य कार्य",
    avoid: "सावधानी",
    mantra: "बीज मंत्र",
    choghadiyaPrompt: "सूर्योदय-सूर्यास्त पर आधारित सटीक चौघड़िया और राहु काल चाहिए?",
    choghadiyaLink: "आज का चौघड़िया देखें →",
    choghadiyaHref: "/hi/choghadiya/",
    sourcePath: "/hi/panchang",
    prompt:
      "आज के पंचांग के अनुसार मेरे काम के लिए सबसे शुभ होरा कौन-सी है? कृपया मेरे शहर के लिए ग्रह होरा का समय देखें।",
  },
} as const;

type HoraCopy = (typeof HORA_COPY)[HoraLanguage];

/** The hora in the page language (Hindi text over the English definition). */
export function localizedHora(hora: HoraDef, language: HoraLanguage): HoraDef {
  const hi = language === "hi" ? HORAS_HI[hora.id] : undefined;
  return hi ? { ...hora, ...hi } : hora;
}

export default function HoraRadarStudio({ language = "en" }: { language?: HoraLanguage }) {
  const [selectedId, setSelectedId] = useState<string>("guru");
  const copy = HORA_COPY[language];

  const hora = useMemo<HoraDef>(() => {
    return localizedHora(HORAS.find((h) => h.id === selectedId) || HORAS[0], language);
  }, [selectedId, language]);

  const handleConsult = () => {
    const prompt = copy.prompt;
    try {
      safeSessionSet("seo_entry_intent", "panchang_lookup");
      safeSessionSet("seo_entry_path", copy.sourcePath);
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader copy={copy} />
      <HoraSelector selectedId={selectedId} onSelect={setSelectedId} language={language} />
      <HoraDetailCard hora={hora} onConsult={handleConsult} copy={copy} language={language} />
    </div>
  );
}

function StudioHeader({ copy }: { copy: HoraCopy }) {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        {copy.eyebrow}
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">{copy.title}</h2>
      <p className="text-body-small text-text-secondary mt-1">{copy.intro}</p>
    </div>
  );
}

function HoraSelector({
  selectedId,
  onSelect,
  language,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  language: HoraLanguage;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {HORAS.map((h) => {
          const isSelected = h.id === selectedId;
          return (
            <button
              key={h.id}
              type="button"
              onClick={() => onSelect(h.id)}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="text-lg block mb-1">{h.symbol}</span>
              <span className="block text-caption font-bold text-text-primary">
                {language === "hi" ? h.hindi : h.name.split(" ")[0]}
              </span>
              <span className="block text-caption text-text-muted">
                {language === "hi" ? h.name.split(" ")[0] : h.hindi}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HoraDetailCard({
  hora,
  onConsult,
  copy,
  language,
}: {
  hora: HoraDef;
  onConsult: () => void;
  copy: HoraCopy;
  language: HoraLanguage;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/panchang"),
    copy.sourcePath,
    language
  );
  const heading = language === "hi" ? hora.name : `${hora.name} (${hora.hindi})`;
  const nature = language === "hi" ? NATURE_HI[hora.nature] : hora.nature;

  const badgeColor =
    hora.nature === "Most Auspicious"
      ? "bg-success/10 text-success"
      : hora.nature === "Auspicious"
        ? "bg-intent-brand/10 text-intent-brand"
        : hora.nature === "Neutral / Specific"
          ? "bg-warning/10 text-warning"
          : "bg-surface-raised text-text-muted";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {hora.symbol} {heading}
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {nature}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            {copy.governedBy} <strong className="text-text-primary">{hora.planet}</strong> &middot;{" "}
            {copy.chaldean}
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex min-h-11 items-center justify-center rounded-lg border border-border-interactive bg-surface-raised px-5 py-2.5 text-button font-bold text-text-primary hover:bg-surface-warm transition-colors"
        >
          {copy.cta}
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-2">
            {copy.favorable}
          </p>
          <ul className="space-y-1.5 text-caption text-text-primary">
            {hora.bestFor.map((item, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-success">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
          <p className="text-caption font-bold uppercase tracking-wider text-warning mb-2">
            {copy.avoid}
          </p>
          <p className="text-caption text-text-primary leading-relaxed mb-4">{hora.avoidFor}</p>
          <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
            {copy.mantra}
          </p>
          <p className="text-caption font-serif italic text-text-primary">
            &quot;{hora.mantra}&quot;
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">{copy.choghadiyaPrompt}</span>
        <Link
          href={copy.choghadiyaHref}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          {copy.choghadiyaLink}
        </Link>
      </div>
    </div>
  );
}
